// Native push notifications bridge.
//
// Wraps @capacitor/push-notifications and routes the resulting native FCM
// (Android) / APNs (iOS) tokens to the same backend endpoint that the web
// PWA already uses: POST /api/notifications/push/tokens.
//
// On web, every export here is a safe no-op — the existing
// firebaseMessaging.js flow handles browser pushes.

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { axiosInstance } from '../config/axios';
import { isNative, platform } from './native';

let _initDone = false;

/** The phone's notification permission for Backyard, in the same terms as the
 *  web helper: 'granted' | 'denied' | 'default'. */
export async function nativePushPermission() {
  if (!isNative()) return 'unsupported';
  try {
    const { receive } = await PushNotifications.checkPermissions();
    if (receive === 'granted') return 'granted';
    if (receive === 'denied') return 'denied';
    return 'default';
  } catch {
    return 'default';
  }
}

async function _registerTokenWithBackend(token) {
  try {
    await axiosInstance.post('/notifications/push/tokens', {
      token,
      platform: platform(),  // 'ios' or 'android'
      user_agent: `Backyard/${platform()} Capacitor`,
    });
    localStorage.setItem('byard-push-enabled', '1');
    return true;
  } catch (e) {
    console.warn('[nativePush] backend register failed:', e?.message);
    return false;
  }
}

/**
 * Request permission and register for native push. Idempotent across calls.
 * Returns { ok, status } similar to the web enablePush() helper.
 *
 * @param {(payload: any) => void} [onTap] — invoked when the user taps a
 *     notification (foreground or background). Use to deep-link.
 * @param {(notif: any) => void} [onForeground] — invoked when a push arrives
 *     while the app is in the foreground (no system banner is shown by default
 *     on iOS in this case — caller can render their own toast).
 */
export async function enableNativePush({ onTap, onForeground } = {}) {
  if (!isNative()) return { ok: false, status: 'not-native' };
  if (_initDone) return { ok: true, status: 'already-initialised' };
  _initDone = true;

  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      return { ok: false, status: perm.receive };
    }

    PushNotifications.addListener('registration', (token) => {
      // token.value is the FCM (Android) or APNs (iOS) registration token.
      _registerTokenWithBackend(token?.value).catch(() => {});
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.warn('[nativePush] registrationError', err);
    });

    if (typeof onForeground === 'function') {
      PushNotifications.addListener('pushNotificationReceived', onForeground);
    }

    if (typeof onTap === 'function') {
      PushNotifications.addListener('pushNotificationActionPerformed', (act) => {
        // act.notification.data carries our deep_link key (set in services/push.py).
        onTap(act?.notification || {});
      });
    }

    await PushNotifications.register();
    return { ok: true, status: 'granted' };
  } catch (e) {
    _initDone = false;
    console.warn('[nativePush] enable failed:', e?.message);
    return { ok: false, status: 'error' };
  }
}

/** Best-effort unregister on logout. The backend will also auto-prune dead
 *  tokens on next FCM dispatch, so failures here are non-fatal. */
export async function disableNativePush() {
  if (!isNative()) return;
  try {
    await PushNotifications.removeAllListeners();
    _initDone = false;
    localStorage.removeItem('byard-push-enabled');
  } catch {
    // ignore
  }
}

/** Helper: figure out the right route from a tapped notification's data. */
export function deepLinkFromNotification(notification) {
  const d = notification?.data || {};
  return d.deep_link || d.deepLink || null;
}

// Re-export the platform check for callers that already import this module.
export { Capacitor };
